import React from "react";
import { CopyOutlined, MailOutlined } from "@ant-design/icons";
import { App } from "antd5";

import { ButtonLink } from "components/actions/Links";
import TextButton from "components/actions/TextButton";
import Button from "components/ui/button/Button";
import Tooltip from "components/ui/tooltip/Tooltip";
import { copyToClipboard } from "lib/clipboard";
import CtaPopover from "lib/core_components/CtaPopover";
import { generateEmailLink } from "lib/emailUtils";
import { useLocalStorage } from "../../lib/hooks/useLocalStorage";
import { OutreachTrackingEvent, useOutreachTracking } from "./tracking";

import css from "./OutreachResult.module.scss";

const EMAIL_SETUP_LINK = "https://help.stotles.com/setting-up-your-email-client";

export function OutreachResult({
  subject,
  body,
  email,
  onAction,
}: {
  subject: string;
  body: string;
  email: string;
  onAction?: () => void;
}) {
  const { track } = useOutreachTracking();
  const [isHidingEmailSetupHelp, setIsHidingEmailSetupHelp] = useLocalStorage(
    "hideEmailSetupHelp",
    false,
  );

  const { message } = App.useApp();

  const copyContent = async (content: string, trackingEvent: OutreachTrackingEvent) => {
    const copied = await copyToClipboard({ plain: content });

    if (!copied) {
      return message.error("Could not copy to clipboard");
    }

    track(trackingEvent);
    onAction?.();
    void message.success("Copied to clipboard!");
  };

  // The message is channel-agnostic, so copying it whole is the primary action — opening a
  // mail client is just one of the places it can go.
  const copyMessageBtn = (
    <Tooltip content="Copy message">
      <Button
        aria-label="Copy message"
        className={css.actionBtn}
        onClick={() => copyContent([subject, body].filter(Boolean).join("\n\n"), "copyMessage")}
      >
        <CopyOutlined />
      </Button>
    </Tooltip>
  );

  const openEmailBtn = (
    <Button title="Open in your email client" className={css.actionBtn}>
      <a
        href={generateEmailLink({ to: email, subject, body })}
        target="_blank"
        onClick={() => {
          track("sendEmail");
          onAction?.();
        }}
        rel="noreferrer"
      >
        <MailOutlined />
      </a>
    </Button>
  );

  return (
    <div className={css.results}>
      <div>
        {email && (
          <div
            className={css.messageContent}
            aria-label="to"
            role="button"
            onClick={async () => copyContent(email, "copyEmail")}
            title="Copy to clipboard"
          >
            <span>To: {email}</span>
            <CopyOutlined className={css.copyIcon} />
          </div>
        )}
        {subject && (
          <div
            className={css.messageContent}
            aria-label="subject"
            role="button"
            onClick={async () => copyContent(subject, "copySubject")}
            title="Copy to clipboard"
          >
            <span>Subject: {subject}</span>
            <CopyOutlined className={css.copyIcon} />
          </div>
        )}

        <div
          className={css.messageContent}
          aria-label="body"
          role="button"
          onClick={() => copyContent(body, "copyBody")}
          title="Copy to clipboard"
        >
          <span>{body}</span>
          <CopyOutlined className={css.copyIcon} />
        </div>
      </div>
      <div className={css.resultsActions}>
        {copyMessageBtn}
        {!isHidingEmailSetupHelp ? (
          <CtaPopover
            title="Want to send this by email?"
            content="Check out our helpful guides on setting up your default email client."
            placement="left"
            buttons={[
              <ButtonLink
                targetType="external"
                to={EMAIL_SETUP_LINK}
                key="help"
                onClick={() => {
                  track("visitEmailSetupHelp", {
                    destination: EMAIL_SETUP_LINK,
                  });
                }}
              >
                View guide
              </ButtonLink>,
              <TextButton
                onClick={() => setIsHidingEmailSetupHelp(true)}
                key="dismiss"
                className={css.blackBtn}
              >
                Don't show again
              </TextButton>,
            ]}
            overlayClassName={css.setupHelp}
          >
            {openEmailBtn}
          </CtaPopover>
        ) : (
          <Tooltip content="Open in your email client">{openEmailBtn}</Tooltip>
        )}
      </div>
    </div>
  );
}
